import React, { useState, useEffect, useCallback } from 'react';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { setSessionData } from './utils/utils';

const firebaseConfig = {
  apiKey: "AIzaSyCs4ynwRb8q-ftaWnGaBCdWZT1kunIMu90",
  authDomain: "actmobile-dda9d.firebaseapp.com",
  databaseURL: "https://actmobile-dda9d.firebaseio.com",
  projectId: "actmobile-dda9d",
  storageBucket: "actmobile-dda9d.firebasestorage.app",
  messagingSenderId: "728762119993",
  appId: "1:728762119993:web:cce4ae0bd14e1d21cb1ae8",
  measurementId: "G-JFP5KPRZH1"
};

const VAPID_KEY = 'BLZGG7AHl_aM46vcfmCeXr69Nv-tNuT5bqllDcmzuJwgCXEoH4H3u8VXGvz8M3BWElXyZnvvkyivpn8dKw7HOWg';
const FCM_TOKEN_STORAGE_KEY = 'fcm_token';
const NOTIFICATION_HISTORY_KEY = 'notification_history';
const PERMISSION_STATE_KEY = 'notification_permission_state';
const LAST_PERMISSION_CHECK_KEY = 'last_permission_check';

// Global Firebase app instance to avoid multiple initializations
let firebaseApp = null;
let messagingInstance = null;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const getPlatformInfo = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /android/i.test(ua);
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  const isChrome = /chrome|chromium|crios/i.test(ua);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  const isSimulator = window.navigator.userAgent.includes('iPhone Simulator') || 
                      window.navigator.userAgent.includes('iPad Simulator') ||
                      (isIOS && (window.location.hostname === 'localhost' || window.location.port === '3000'));
  
  let iOSVersion = null;
  if (isIOS) {
    const match = navigator.userAgent.match(/OS (\d+)_?(\d+)?/);
    if (match) {
      iOSVersion = {
        major: parseInt(match[1], 10),
        minor: match[2] ? parseInt(match[2], 10) : 0
      };
    }
  }
  
  return {
    isIOS,
    isAndroid,
    isSafari,
    isChrome,
    isMobile: isIOS || isAndroid,
    isDesktop: !(isIOS || isAndroid),
    isStandalone,
    isSimulator,
    iOSVersion
  };
};

const checkBrowserSupport = async () => {
  const platform = getPlatformInfo();
  const hasNotification = 'Notification' in window;
  const hasServiceWorker = 'serviceWorker' in navigator;
  const hasPushManager = 'PushManager' in window;
  const isSecure = window.isSecureContext;
  let unsupportedReason = '';
  let iosWebPushSupported = false;
  
  // Check if Firebase messaging is supported in this environment
  let firebaseMessagingSupported = false;
  try {
    firebaseMessagingSupported = await isSupported();
  } catch (err) {
    console.error('Error checking Firebase support:', err);
  }

  // For iOS, check for iOS 16.4+ for web push support
  if (platform.isIOS && platform.iOSVersion) {
    if (platform.iOSVersion.major > 16 || (platform.iOSVersion.major === 16 && platform.iOSVersion.minor >= 4)) {
      iosWebPushSupported = true;
    }
  }
  
  // Check for simulator - use fallback
  if (platform.isSimulator) {
    unsupportedReason = 'iOS Simulator detected. Using in-app notifications with immediate sound.';
    return {
      hasBasicSupport: false,
      platformSupport: {
        ios: false,
        android: false,
        desktop: false
      },
      platform,
      unsupportedReason,
      firebaseMessagingSupported: false,
      shouldUseFallback: true
    };
  }

  // Check for iOS without web push support
  if (platform.isIOS && !iosWebPushSupported) {
    unsupportedReason = 'iOS 16.4+ is required for push notifications. Please update your iOS version.';
  } else if (platform.isIOS && !platform.isStandalone) {
    unsupportedReason = 'For push notifications on iOS, please add this app to your home screen.';
  } else if (!hasNotification) {
    unsupportedReason = 'Your browser doesn\'t support notifications.';
  } else if (!hasServiceWorker) {
    unsupportedReason = 'Your browser doesn\'t support service workers required for notifications.';
  } else if (!hasPushManager) {
    unsupportedReason = 'Your browser doesn\'t support the Push API required for notifications.';
  } else if (!isSecure) {
    unsupportedReason = 'Notifications require a secure connection (HTTPS).';
  } else if (!firebaseMessagingSupported) {
    unsupportedReason = 'Push notifications are not supported in this browser.';
  }

  const shouldUseFallback = platform.isSimulator || 
                           (platform.isIOS && (!iosWebPushSupported || !platform.isStandalone)) ||
                           !firebaseMessagingSupported ||
                           !hasNotification ||
                           !hasServiceWorker ||
                           !hasPushManager ||
                           !isSecure;

  return {
    hasBasicSupport: hasNotification && hasServiceWorker && hasPushManager && isSecure && firebaseMessagingSupported && !shouldUseFallback,
    platformSupport: {
      ios: platform.isIOS ? (iosWebPushSupported && platform.isStandalone && firebaseMessagingSupported) : false,
      android: platform.isAndroid && hasNotification && hasServiceWorker && hasPushManager && firebaseMessagingSupported,
      desktop: platform.isDesktop && hasNotification && hasServiceWorker && hasPushManager && firebaseMessagingSupported
    },
    platform,
    unsupportedReason,
    firebaseMessagingSupported,
    shouldUseFallback
  };
};

// Initialize Firebase only once
const initializeFirebase = () => {
  if (!firebaseApp) {
    try {
      firebaseApp = initializeApp(firebaseConfig);
      console.log('Firebase app initialized successfully');
    } catch (err) {
      console.error('Error initializing Firebase app:', err);
      throw err;
    }
  }
  return firebaseApp;
};

// Initialize messaging only once
const initializeMessaging = async () => {
  if (!messagingInstance) {
    try {
      const app = initializeFirebase();
      const isMessagingSupported = await isSupported();
      
      if (!isMessagingSupported) {
        throw new Error('Firebase messaging not supported in this environment');
      }
      
      messagingInstance = getMessaging(app);
      console.log('Firebase messaging initialized successfully');
    } catch (err) {
      console.error('Error initializing Firebase messaging:', err);
      throw err;
    }
  }
  return messagingInstance;
};

const PushNotificationComponent = () => {
  const [token, setToken] = useState('');
  const [notificationMessage, setNotificationMessage] = useState(null);
  const [error, setError] = useState('');
  const [isPermissionGranted, setIsPermissionGranted] = useState(false);
  const [supportInfo, setSupportInfo] = useState(null);
  const [messaging, setMessaging] = useState(null);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [instructionsExpanded, setInstructionsExpanded] = useState(false);
  const [fallbackMode, setFallbackMode] = useState(false);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [audioUnlocked, setAudioUnlocked] = useState(false);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // IMMEDIATE audio initialization and unlocking system for iOS
  useEffect(() => {
    const initializeImmediateAudio = () => {
      try {
        console.log('Initializing immediate audio system...');
        
        // Create multiple audio instances for reliability
        const createAudioInstance = (id) => {
          const audio = new Audio('/Voicy_truckhorn.mp3');
          audio.setAttribute('preload', 'auto');
          audio.setAttribute('playsinline', 'true');
          audio.crossOrigin = 'anonymous';
          audio.volume = 1.0;
          audio.load();
          return audio;
        };
        
        // Create 3 audio instances for maximum reliability
        window._notificationAudio1 = createAudioInstance('primary');
        window._notificationAudio2 = createAudioInstance('backup');
        window._notificationAudio3 = createAudioInstance('emergency');
        
        // Global audio state
        window._audioLastPlayed = 0;
        window._audioUnlocked = false;
        window._pendingSounds = [];
        
        // Pre-unlock function that runs on ANY user interaction
        const unlockAudioImmediately = () => {
          if (window._audioUnlocked) return;
          
          console.log('Attempting immediate audio unlock...');
          
          const unlockAudio = (audio, name) => {
            try {
              const originalVolume = audio.volume;
              audio.volume = 0.1;
              audio.currentTime = 0;
              
              const playPromise = audio.play();
              if (playPromise) {
                playPromise.then(() => {
                  audio.pause();
                  audio.currentTime = 0;
                  audio.volume = originalVolume;
                  console.log(`Audio ${name} unlocked successfully`);
                }).catch(() => {
                  audio.volume = originalVolume;
                });
              }
            } catch (e) {
              console.log(`Audio ${name} unlock failed:`, e);
            }
          };
          
          // Unlock all audio instances
          unlockAudio(window._notificationAudio1, 'primary');
          unlockAudio(window._notificationAudio2, 'backup');
          unlockAudio(window._notificationAudio3, 'emergency');
          
          window._audioUnlocked = true;
          setAudioUnlocked(true);
          
          // Play any pending sounds
          if (window._pendingSounds.length > 0) {
            console.log(`Playing ${window._pendingSounds.length} pending sounds`);
            window._pendingSounds.forEach(() => {
              setTimeout(() => playImmediateSound(), 100);
            });
            window._pendingSounds = [];
          }
          
          // Remove listeners after first unlock
          document.removeEventListener('click', unlockAudioImmediately);
          document.removeEventListener('touchstart', unlockAudioImmediately);
          document.removeEventListener('touchend', unlockAudioImmediately);
          document.removeEventListener('keydown', unlockAudioImmediately);
        };
        
        // Set up multiple unlock triggers
        document.addEventListener('click', unlockAudioImmediately, { passive: true });
        document.addEventListener('touchstart', unlockAudioImmediately, { passive: true });
        document.addEventListener('touchend', unlockAudioImmediately, { passive: true });
        document.addEventListener('keydown', unlockAudioImmediately, { passive: true });
        
        // For iOS, also try to unlock on page visibility change
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) {
            // Page just went hidden: pause any sounds still playing so they
            // don't continue in the background. iOS/WebKit usually blocks
            // background audio anyway; this just ensures we don't leak
            // sound through moments where the guard in playImmediateSound
            // didn't catch an already-started playback.
            try {
              [window._notificationAudio1, window._notificationAudio2, window._notificationAudio3]
                .forEach(a => { if (a && !a.paused) { a.pause(); a.currentTime = 0; } });
            } catch (e) {
              console.log('Failed to pause audio on hide:', e);
            }
          } else if (!window._audioUnlocked) {
            setTimeout(unlockAudioImmediately, 100);
          }
        });
        
        setAudioReady(true);
        console.log('Immediate audio system initialized');
        
        return () => {
          document.removeEventListener('click', unlockAudioImmediately);
          document.removeEventListener('touchstart', unlockAudioImmediately);
          document.removeEventListener('touchend', unlockAudioImmediately);
          document.removeEventListener('keydown', unlockAudioImmediately);
        };
        
      } catch (e) {
        console.error('Failed to initialize immediate audio:', e);
        setAudioReady(false);
      }
    };

    const cleanup = initializeImmediateAudio();
    return cleanup;
  }, []);

  // Track user interaction for iOS permission requirements
  useEffect(() => {
    const handleUserInteraction = () => {
      setIsUserInteracting(true);
    };

    document.addEventListener('click', handleUserInteraction, { passive: true });
    document.addEventListener('touchend', handleUserInteraction, { passive: true });

    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('touchend', handleUserInteraction);
    };
  }, []);

  // Load stored permission state
  const loadStoredPermissionState = useCallback(() => {
    try {
      const storedState = localStorage.getItem(PERMISSION_STATE_KEY);
      if (storedState) {
        const state = JSON.parse(storedState);
        const currentPermission = window.Notification ? window.Notification.permission : 'default';
        
        // Only trust stored state if it matches current permission
        if (state.permission === currentPermission) {
          setIsPermissionGranted(currentPermission === 'granted');
          if (state.fallbackMode) {
            setFallbackMode(true);
          }
          return true;
        }
      }
    } catch (err) {
      console.error('Error loading stored permission state:', err);
    }
    return false;
  }, []);

  // Save permission state
  const savePermissionState = useCallback((permission, inFallbackMode = false) => {
    try {
      const state = {
        permission,
        fallbackMode: inFallbackMode,
        timestamp: Date.now(),
        platform: getPlatformInfo()
      };
      localStorage.setItem(PERMISSION_STATE_KEY, JSON.stringify(state));
      localStorage.setItem(LAST_PERMISSION_CHECK_KEY, Date.now().toString());
    } catch (err) {
      console.error('Error saving permission state:', err);
    }
  }, []);

  // Initialize notifications with proper state management
  useEffect(() => {
    const initializeNotifications = async () => {
      try {
        // First load any stored state
        const hasStoredState = loadStoredPermissionState();
        
        const support = await checkBrowserSupport();
        setSupportInfo(support);
        
        if (support.shouldUseFallback) {
          setFallbackMode(true);
          setError(support.unsupportedReason);
          loadNotificationHistory();
          if (!hasStoredState) {
            savePermissionState('default', true);
          }
          return;
        }

        // Check current permission state
        const currentPermission = window.Notification ? window.Notification.permission : 'default';
        setIsPermissionGranted(currentPermission === 'granted');

        if (currentPermission === 'granted') {
          try {
            const msgInstance = await initializeMessaging();
            setMessaging(msgInstance);
            await fetchAndStoreToken(msgInstance);
            loadNotificationHistory();
            savePermissionState('granted', false);
          } catch (err) {
            console.error('Error initializing messaging after permission granted:', err);
            handleFallbackMode('Error initializing messaging system');
          }
        } else {
          // Don't auto-request permission - wait for user interaction
          console.log('Notification permission not granted, waiting for user action');
          if (!hasStoredState) {
            savePermissionState(currentPermission, false);
          }
        }
      } catch (err) {
        console.error('Error initializing notifications:', err);
        handleFallbackMode('Unable to initialize notification system');
      }
    };

    initializeNotifications();
  }, [loadStoredPermissionState, savePermissionState]);

  const handleFallbackMode = (errorMessage) => {
    setFallbackMode(true);
    setError(errorMessage);
    loadNotificationHistory();
    savePermissionState('denied', true);
  };

  // IMMEDIATE sound playing function - plays sound RIGHT NOW
  const playImmediateSound = useCallback(() => {
    try {
      // Session guard: if the user has no active officer session, do not play.
      // Prevents post-logout/post-session-expiry sounds from leaking through.
      if (!localStorage.getItem('userID') || !localStorage.getItem('token')) {
        console.log('Sound suppressed: no active session');
        return;
      }

      // Visibility guard: if the page is hidden (tab backgrounded, PWA
      // minimized, screen off), do not force audio through. iOS/WebKit
      // blocks most background autoplay anyway; this avoids queued
      // "catch-up" playback when the user returns to the app.
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        console.log('Sound suppressed: page hidden');
        return;
      }

      const now = Date.now();

      // Prevent sound spam (500ms minimum between sounds - reduced for faster response)
      if (window._audioLastPlayed && now - window._audioLastPlayed < 500) {
        console.log('Sound spam prevention: too soon since last sound');
        return;
      }
      
      window._audioLastPlayed = now;
      
      // If audio not unlocked yet, queue the sound
      if (!window._audioUnlocked) {
        console.log('Audio not unlocked yet, queueing sound...');
        window._pendingSounds = window._pendingSounds || [];
        window._pendingSounds.push(now);
        return;
      }
      
      // Try to play with multiple fallbacks
      const tryPlayAudio = (audio, name) => {
        return new Promise((resolve, reject) => {
          if (!audio || audio.error) {
            reject(new Error(`Audio ${name} not available`));
            return;
          }
          
          try {
            audio.currentTime = 0;
            audio.volume = 1.0;
            
            const playPromise = audio.play();
            if (playPromise) {
              playPromise.then(() => {
                console.log(`Immediate sound played successfully with ${name}`);
                resolve();
              }).catch(err => {
                reject(err);
              });
            } else {
              console.log(`Direct play attempted with ${name}`);
              resolve();
            }
          } catch (e) {
            reject(e);
          }
        });
      };
      
      // Try primary audio first, then backups
      tryPlayAudio(window._notificationAudio1, 'primary')
        .catch(() => tryPlayAudio(window._notificationAudio2, 'backup'))
        .catch(() => tryPlayAudio(window._notificationAudio3, 'emergency'))
        .catch((err) => {
          console.log('All audio instances failed, creating new one:', err);
          
          // Ultimate fallback: create new audio and try to play
          try {
            const emergencyAudio = new Audio('/Voicy_truckhorn.mp3');
            emergencyAudio.volume = 1.0;
            emergencyAudio.play().catch(() => {
              console.log('Emergency audio also failed - driver will need to check app manually');
            });
          } catch (emergencyErr) {
            console.error('Emergency audio creation failed:', emergencyErr);
          }
        });
      
    } catch (err) {
      console.error('Exception in playImmediateSound:', err);
    }
  }, []);

  const fetchAndStoreToken = async (messagingInstance) => {
    // Check if we already have a valid token
    const storedToken = localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
    if (storedToken) {
      setToken(storedToken);
      console.log('Using stored FCM token');
      return storedToken;
    }
    
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        throw new Error("Push notifications not supported in this browser");
      }

      // Wait for Firebase messaging service worker to be ready
      let messagingSWRegistration = null;
      let attempts = 0;
      const maxAttempts = 5;
      
      while (!messagingSWRegistration && attempts < maxAttempts) {
        try {
          const registrations = await navigator.serviceWorker.getRegistrations();
          
          for (const reg of registrations) {
            if (reg.active && reg.active.scriptURL.includes('firebase-messaging-sw.js')) {
              messagingSWRegistration = reg;
              break;
            }
          }
          
          if (!messagingSWRegistration) {
            console.log(`Attempt ${attempts + 1}: Waiting for Firebase messaging service worker...`);
            await new Promise(resolve => setTimeout(resolve, 1000));
            attempts++;
          }
        } catch (regError) {
          console.error('Error checking service worker registrations:', regError);
          attempts++;
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      if (!messagingSWRegistration) {
        throw new Error('Firebase messaging service worker not found after multiple attempts');
      }
      
      // Get or create push subscription
      let pushSubscription = await messagingSWRegistration.pushManager.getSubscription();
      
      if (!pushSubscription) {
        try {
          const applicationServerKey = urlBase64ToUint8Array(VAPID_KEY);
          pushSubscription = await messagingSWRegistration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: applicationServerKey
          });
          
          if (!pushSubscription.endpoint) {
            throw new Error("Push subscription has no endpoint");
          }
          
          console.log('Push subscription created successfully');
        } catch (subscriptionError) {
          if (subscriptionError.name === 'NotAllowedError') {
            throw new Error("Permission denied for push subscription");
          }
          throw new Error(`Push subscription failed: ${subscriptionError.message}`);
        }
      }
      
      // Get FCM token
      const currentToken = await getToken(messagingInstance, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: messagingSWRegistration
      });
      
      if (currentToken) {
        setToken(currentToken);
        localStorage.setItem(FCM_TOKEN_STORAGE_KEY, currentToken);
        setSessionData(currentToken, 'uniqueDeviceId');
        
        const notification = {
          title: 'Driver Alert System Ready',
          body: 'You will now receive immediate audio alerts when the driver arrives.',
          timestamp: new Date().toLocaleTimeString(),
          type: 'success',
          id: Date.now()
        };
        setNotificationMessage(notification);
        updateNotificationHistory(notification);
        
        // IMMEDIATE sound on setup success
        playImmediateSound();
        
        console.log('FCM token obtained successfully');
        return currentToken;
      } else {
        throw new Error('No FCM token available');
      }
    } catch (err) {
      console.error('Error fetching FCM token:', err);
      throw err;
    }
  };

  const loadNotificationHistory = () => {
    try {
      const history = JSON.parse(localStorage.getItem(NOTIFICATION_HISTORY_KEY)) || [];
      setNotificationHistory(history);
    } catch (err) {
      setNotificationHistory([]);
    }
  };

  const updateNotificationHistory = useCallback((notification) => {
    setNotificationHistory(prevHistory => {
      // Avoid duplicates
      if (prevHistory.length > 0) {
        const lastNotification = prevHistory[0];
        if (lastNotification.title === notification.title && 
            lastNotification.body === notification.body &&
            Math.abs(lastNotification.id - notification.id) < 3000) {
          return prevHistory;
        }
      }
      const updated = [notification, ...prevHistory].slice(0, 10);
      localStorage.setItem(NOTIFICATION_HISTORY_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // USER-INITIATED permission request (no automatic requests)
  const requestPermissionAndFetchToken = async () => {
    if (!isUserInteracting && supportInfo?.platform?.isIOS) {
      setError('Please tap the notification icon to enable notifications');
      return;
    }

    if (fallbackMode) {
      handleAlternativeNotificationOption();
      return;
    }
    
    try {
      console.log('Requesting notification permission...');
      const permission = await Notification.requestPermission();
      
      setIsPermissionGranted(permission === 'granted');
      savePermissionState(permission, false);
      
      if (permission === 'granted') {
        try {
          const msgInstance = await initializeMessaging();
          setMessaging(msgInstance);
          await fetchAndStoreToken(msgInstance);
          
          // Show success notification (silent to avoid double sound)
          if (supportInfo?.platform?.isIOS) {
            try {
              const iosTestNotification = new Notification('Driver Alert System Ready', {
                body: 'You will now receive immediate audio alerts when the driver arrives.',
                icon: '/logo192.png',
                silent: true // We already played our custom sound
              });
              setTimeout(() => {
                iosTestNotification.close();
              }, 3000);
            } catch (err) {
              console.log('Could not show test notification:', err);
            }
          }
        } catch (err) {
          console.error('Error setting up notifications after permission granted:', err);
          handleFallbackMode('Unable to complete notification setup');
        }
      } else if (permission === 'denied') {
        setError('Notification permission denied. You can re-enable in browser settings.');
        setShowInstructions(true);
        setInstructionsExpanded(true);
        savePermissionState('denied', false);
      } else {
        setError('Notification permission not granted.');
        savePermissionState('default', false);
      }
    } catch (err) {
      console.error('Permission request failed:', err);
      handleFallbackMode('Permission request failed. Using in-app alerts.');
    }
  };

  const handleAlternativeNotificationOption = () => {
    const { platform } = supportInfo || {};
    let message = 'You\'ll receive immediate audio alerts when driver arrives.';
    
    if (platform?.isIOS) {
      if (platform?.isStandalone) {
        message = 'Driver alerts will play immediately with audio in the app.';
      } else {
        message = 'For instant driver alerts, add this app to your home screen and reopen it.';
      }
    }
    
    const notification = {
      title: 'Driver Alert System Active',
      body: message,
      timestamp: new Date().toLocaleTimeString(),
      type: 'info',
      id: Date.now()
    };
    setNotificationMessage(notification);
    updateNotificationHistory(notification);
    
    // IMMEDIATE sound for fallback notification
    playImmediateSound();
  };

  const toggleNotificationHistory = () => setShowHistory(prev => !prev);
  const toggleInstructions = () => setInstructionsExpanded(prev => !prev);

  // Foreground message listener with IMMEDIATE sound
  useEffect(() => {
    if (!messaging || fallbackMode) return;
    
    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('🚨 DRIVER ARRIVAL - Foreground message received:', payload);
      
      if (payload.notification) {
        // IMMEDIATE sound - driver has arrived!
        console.log('🔊 Playing immediate driver arrival alert...');
        playImmediateSound();
        
        const notification = {
          title: payload.notification.title || 'Driver Alert',
          body: payload.notification.body || 'Your driver has arrived!',
          timestamp: new Date().toLocaleTimeString(),
          type: 'foreground',
          id: Date.now()
        };
        setNotificationMessage(notification);
        updateNotificationHistory(notification);
      }
    });
    
    return () => unsubscribe();
  }, [messaging, updateNotificationHistory, fallbackMode, playImmediateSound]);

  // Background message listener via Service Worker with IMMEDIATE sound
  useEffect(() => {
    if (fallbackMode) return;
    
    const messageHandler = (event) => {
      if (event.data?.type === 'BACKGROUND_NOTIFICATION' && event.data.payload?.notification) {
        console.log('🚨 DRIVER ARRIVAL - Background notification received:', event.data.payload);
        
        // IMMEDIATE sound - driver has arrived!
        console.log('🔊 Playing immediate driver arrival alert from background...');
        playImmediateSound();
        
        const notification = {
          title: event.data.payload.notification.title || 'Driver Alert',
          body: event.data.payload.notification.body || 'Your driver has arrived!',
          timestamp: new Date().toLocaleTimeString(),
          type: 'background',
          id: Date.now()
        };
        setNotificationMessage(notification);
        updateNotificationHistory(notification);
      } else if (event.data?.type === 'PLAY_NOTIFICATION_SOUND') {
        // Direct sound play request from service worker
        console.log('🔊 Service worker requesting immediate sound play...');
        playImmediateSound();
      }
    };
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', messageHandler);
    }
    
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', messageHandler);
      }
    };
  }, [updateNotificationHistory, fallbackMode, playImmediateSound]);

  const getNotificationBackgroundColor = (type) => {
    switch(type) {
      case 'foreground': return '#e3f2fd';
      case 'background': return '#e8f5e9';
      case 'info': return '#fff3e0';
      case 'error': return '#ffebee';
      case 'success': return '#e8f5e9';
      case 'fallback': return '#f3e5f5';
      default: return '#f8f9fa';
    }
  };

  const renderNotificationInstructions = () => {
    const { platform } = supportInfo || {};
    let instructionsContent = null;
    
    if (platform?.isIOS && platform?.isStandalone) {
      instructionsContent = (
        <>
          <h4 style={styles.instructionTitle}>Enable Driver Alerts on iOS</h4>
          {instructionsExpanded && (
            <ol style={styles.instructionList}>
              <li>Open your device's Settings app</li>
              <li>Scroll down and tap on this app's name</li>
              <li>Tap "Notifications"</li>
              <li>Enable "Allow Notifications"</li>
              <li>Truck horn will play immediately when driver arrives</li>
              <li>Return to this app and refresh</li>
            </ol>
          )}
        </>
      );
    } else if (platform?.isIOS) {
      instructionsContent = (
        <>
          <h4 style={styles.instructionTitle}>Enable Driver Alerts on iOS</h4>
          {instructionsExpanded && (
            <>
              <ol style={styles.instructionList}>
                <li>To enable instant driver alerts:</li>
                <li>Tap the share icon in Safari (square with arrow)</li>
                <li>Scroll down and tap "Add to Home Screen"</li>
                <li>Tap "Add" in the top right corner</li>
                <li>Open the app from your home screen</li>
                <li>Tap the notification bell icon and allow notifications</li>
                <li>Truck horn will play the moment your driver arrives</li>
              </ol>
              <p style={styles.instructionNote}>
                Note: iOS requires version 16.4 or later. 
                Audio alert plays immediately when driver arrives - no need to close notification.
              </p>
            </>
          )}
        </>
      );
    } else {
      instructionsContent = (
        <>
          <h4 style={styles.instructionTitle}>Enable Driver Alerts</h4>
          {instructionsExpanded && (
            <ol style={styles.instructionList}>
              <li>Tap the bell icon above</li>
              <li>Allow notifications when prompted</li>
              <li>You'll hear truck horn immediately when driver arrives</li>
              <li>No need to close notification - sound plays instantly</li>
            </ol>
          )}
        </>
      );
    }
    
    return (
      <div style={{ ...styles.instructionsContainer, ...(instructionsExpanded ? {} : styles.instructionsCollapsed) }}>
        <div style={styles.instructionsHeader} onClick={toggleInstructions}>
          {instructionsContent}
          <div style={styles.expandCollapseIcon}>
            {instructionsExpanded ? '▼' : '▶'}
          </div>
        </div>
        {instructionsExpanded && (
          <div style={styles.instructionActions}>
            <button onClick={() => { setInstructionsExpanded(false); setShowInstructions(false); }} style={styles.instructionButton}>
              Got it
            </button>
            <button onClick={() => { playImmediateSound(); }} style={styles.testSoundButton}>
              🚨 Test Driver Alert
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderNotificationIcon = () => {
    const isSupported = supportInfo?.platformSupport.android || 
                        supportInfo?.platformSupport.desktop || 
                        supportInfo?.platformSupport.ios;
    
    let iconColor = "#FFFFFF";
    let statusTooltip = "";
    let statusIcon = null;
    let iconButtonStyle = styles.iconButton;
    
    if (fallbackMode) {
      statusTooltip = "Driver alerts with immediate sound";
      iconButtonStyle = {
        ...styles.iconButton,
        backgroundColor: 'rgba(156, 39, 176, 0.3)',
        borderColor: '#9C27B0'
      };
      statusIcon = (
        <div style={styles.statusIcon} className="notification-status-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#9C27B0">
            <path d="M13 7h-2v4H7v2h4v4h2v-4h4v-2h-4z"/>
          </svg>
        </div>
      );
    } else if (isPermissionGranted && isSupported) {
      statusTooltip = "Driver alerts ready - immediate sound";
      iconButtonStyle = {
        ...styles.iconButton,
        backgroundColor: 'rgba(76, 175, 80, 0.3)',
        borderColor: '#4CAF50'
      };
      statusIcon = (
        <div style={styles.statusIcon} className="notification-status-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#4CAF50">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
        </div>
      );
    } else if (isSupported) {
      iconColor = "#FFD700";
      iconButtonStyle = {
        ...styles.iconButton,
        backgroundColor: 'rgba(255, 215, 0, 0.3)',
        borderColor: '#FFD700'
      };
      statusTooltip = "Tap to enable instant driver alerts";
    } else {
      statusTooltip = supportInfo?.unsupportedReason || "Driver alerts not available";
      iconButtonStyle = {
        ...styles.iconButton,
        backgroundColor: 'rgba(244, 67, 54, 0.3)',
        borderColor: '#F44336'
      };
      statusIcon = (
        <div style={styles.statusIcon} className="notification-status-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#F44336">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </div>
      );
    }
    
    const handleIconClick = () => {
      if (fallbackMode) {
        toggleNotificationHistory();
      } else if (isSupported) {
        if (isPermissionGranted) {
          toggleNotificationHistory();
        } else {
          setShowInstructions(true);
          setInstructionsExpanded(true);
          requestPermissionAndFetchToken();
        }
      } else {
        setShowInstructions(true);
        setInstructionsExpanded(true);
        handleAlternativeNotificationOption();
      }
    };
    
    return (
      <div style={styles.iconContainer}>
        <button
          onClick={handleIconClick}
          style={{ ...iconButtonStyle, ...(isMobile ? styles.iconButtonMobile : {}) }}
          aria-label={statusTooltip}
          title={statusTooltip}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
            style={{ 
              ...styles.icon, 
              ...(isSupported && !isPermissionGranted && !fallbackMode ? styles.iconPulse : {})
            }}
            fill={iconColor}>
            <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z"/>
          </svg>
          {statusIcon}
          {notificationHistory.length > 0 && (
            <span style={{ ...styles.badge, ...(isMobile ? styles.badgeMobile : {}) }}>
              {notificationHistory.length}
            </span>
          )}
        </button>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.headerSection}>
        {renderNotificationIcon()}
      </div>
      <div >
        {notificationMessage && (
          <div
            style={{
              ...styles.notificationInline,
              ...(isMobile ? styles.notificationInlineMobile : {}),
              backgroundColor: getNotificationBackgroundColor(notificationMessage.type)
            }}>
            <div style={styles.notificationContent}>
              <strong style={isMobile ? styles.mobileFontTitle : {}}>
                {notificationMessage.title}
              </strong>
              <p style={isMobile ? styles.mobileFontBody : {}}>
                {notificationMessage.body}
              </p>
            </div>
            <button
              style={{ ...styles.closeButton, ...(isMobile ? styles.closeButtonMobile : {}) }}
              onClick={() => setNotificationMessage(null)}
              aria-label="Close notification">
              ×
            </button>
          </div>
        )}
        {showInstructions && renderNotificationInstructions()}
        {showHistory && (
          <div style={{ ...styles.historyInline, ...(isMobile ? styles.historyInlineMobile : {}) }}>
            <div style={{ ...styles.historyHeader, ...(isMobile ? styles.historyHeaderMobile : {}) }}>
              <h3 style={{ ...styles.historyTitle, ...(isMobile ? styles.historyTitleMobile : {}) }}>
                Driver Alerts
              </h3>
              <div style={styles.historyActions}>
                <button onClick={() => { playImmediateSound(); }} style={styles.testSoundButton}>
                  🚨 Test Alert
                </button>
                <button onClick={() => { setNotificationHistory([]); localStorage.removeItem(NOTIFICATION_HISTORY_KEY); }}
                  style={{ ...styles.clearButton, ...(isMobile ? styles.clearButtonMobile : {}) }}
                  disabled={notificationHistory.length === 0}>
                  Clear All
                </button>
                <button onClick={toggleNotificationHistory}
                  style={{ ...styles.closeButton, ...(isMobile ? styles.closeButtonMobile : {}) }}
                  aria-label="Close history">
                  ×
                </button>
              </div>
            </div>
            <div style={styles.historyList}>
              {notificationHistory.length === 0 ? (
                <div style={styles.emptyHistory}>
                  <p style={isMobile ? styles.mobileFontBody : {}}>
                    No driver alerts yet
                  </p>
                </div>
              ) : (
                notificationHistory.map(notification => (
                  <div key={notification.id}
                    style={{
                      ...styles.historyItem,
                      ...(isMobile ? styles.historyItemMobile : {}),
                      backgroundColor: getNotificationBackgroundColor(notification.type)
                    }}>
                    <div style={styles.historyItemContent}>
                      <strong style={isMobile ? styles.mobileFontTitle : {}}>
                        {notification.title}
                      </strong>
                      <p style={isMobile ? styles.mobileFontBody : {}}>
                        {notification.body}
                      </p>
                      <span style={{ ...styles.historyMeta, ...(isMobile ? styles.historyMetaMobile : {}) }}>
                        {notification.timestamp}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        {error && !showInstructions && (
          <span style={{ ...styles.error, ...(isMobile ? styles.errorMobile : {}) }}>
            {error}
          </span>
        )}
        
        {/* Debug info for development */}
        {process.env.NODE_ENV === 'development' && (
          <div style={styles.debugInfo}>
            Audio Ready: {audioReady ? '✅' : '❌'} | 
            Audio Unlocked: {audioUnlocked ? '✅' : '❌'} | 
            User Interaction: {isUserInteracting ? '✅' : '❌'} |
            Platform: {supportInfo?.platform?.isIOS ? 'iOS' : supportInfo?.platform?.isAndroid ? 'Android' : 'Desktop'}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: { position: 'relative', width: '100%', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif', boxSizing: 'border-box' },
  headerSection: { 
    position: 'fixed', 
    top: 0, 
    left: 0, 
    right: 10, 
    display: 'flex', 
    justifyContent: 'right', 
    alignItems: 'right',
    zIndex: 1000,
    height: '50px'
  },
  iconContainer: { 
    position: 'absolute', 
    zIndex: 1001,
    top: '5px',
    right: '15px'
  },
  iconButton: { 
    background: 'rgba(255, 255, 255, 0.2)', 
    border: '2px solid white', 
    borderRadius: '50%', 
    cursor: 'pointer', 
    width: '36px', 
    height: '36px', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    position: 'relative', 
    padding: '6px', 
    margin: '0',
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.3)'
  },
  iconButtonMobile: { 
    width: '42px', 
    height: '42px', 
    padding: '10px', 
    touchAction: 'manipulation' 
  },
  icon: { 
    width: '22px', 
    height: '22px', 
    transition: 'all 0.3s ease',
    filter: 'drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.5))'
  },
  iconPulse: { 
    animation: 'pulse 2s infinite',
    filter: 'drop-shadow(0px 0px 4px white)'
  },
  statusIcon: { 
    position: 'absolute', 
    right: '-4px', 
    top: '-4px', 
    backgroundColor: 'white', 
    borderRadius: '50%', 
    width: '16px', 
    height: '16px', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
    border: '1px solid #ccc'
  },
  badge: { 
    position: 'absolute', 
    bottom: '-4px', 
    right: '-4px', 
    backgroundColor: '#f44336', 
    color: 'white', 
    borderRadius: '50%', 
    width: '16px', 
    height: '16px', 
    fontSize: '10px', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    fontWeight: 'bold', 
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
    border: '1px solid white'
  },
  badgeMobile: { 
    width: '18px', 
    height: '18px', 
    fontSize: '12px', 
    bottom: '-6px', 
    right: '-6px' 
  },
  contentSection: { width: '100%', marginTop: '50px', boxSizing: 'border-box', padding: '0 12px' },
  notificationInline: { 
    backgroundColor: '#f8f9fa', 
    border: '1px solid #e9ecef', 
    borderRadius: '8px', 
    padding: '12px', 
    marginBottom: '12px', 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'flex-start', 
    width: '100%', 
    boxSizing: 'border-box', 
    transition: 'background-color 0.2s ease', 
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
  },
  notificationInlineMobile: { padding: '10px', borderRadius: '6px' },
  notificationContent: { flex: 1, wordBreak: 'break-word' },
  closeButton: { 
    background: 'none', 
    border: 'none', 
    fontSize: '1.2rem', 
    cursor: 'pointer', 
    padding: '0 2px', 
    minWidth: '28px', 
    minHeight: '28px', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    color: '#666'
  },
  closeButtonMobile: { minWidth: '32px', minHeight: '32px', fontSize: '1.5rem', touchAction: 'manipulation' },
  historyInline: { 
    backgroundColor: 'white', 
    border: '1px solid #e9ecef', 
    borderRadius: '8px', 
    width: '100%', 
    maxHeight: '350px', 
    display: 'flex', 
    flexDirection: 'column', 
    marginBottom: '16px', 
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', 
    boxSizing: 'border-box', 
    overflow: 'hidden'
  },
  historyInlineMobile: { maxHeight: '60vh', borderRadius: '6px' },
  historyHeader: { 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center', 
    padding: '8px 12px', 
    borderBottom: '1px solid #eee', 
    backgroundColor: '#f8f9fa', 
    borderTopLeftRadius: '8px', 
    borderTopRightRadius: '8px' 
  },
  historyHeaderMobile: { padding: '8px 10px' },
  historyTitle: { margin: 0, fontSize: '15px', fontWeight: '600' },
  historyTitleMobile: { fontSize: '15px' },
  historyActions: { display: 'flex', alignItems: 'center', gap: '8px' },
  clearButton: { 
    backgroundColor: 'transparent', 
    border: 'none', 
    color: '#666', 
    fontSize: '12px', 
    cursor: 'pointer', 
    padding: '4px 8px', 
    marginRight: '4px' 
  },
  clearButtonMobile: { fontSize: '12px', padding: '6px 8px' },
  testSoundButton: {
    backgroundColor: '#ff4444', 
    color: 'white', 
    border: 'none', 
    borderRadius: '4px', 
    padding: '4px 8px', 
    cursor: 'pointer', 
    fontSize: '11px',
    fontWeight: 'bold'
  },
  historyList: { 
    overflowY: 'auto', 
    maxHeight: 'calc(350px - 40px)', 
    padding: '0', 
    WebkitOverflowScrolling: 'touch' 
  },
  historyItem: { 
    padding: '10px 12px', 
    borderBottom: '1px solid #eee', 
    display: 'flex', 
    alignItems: 'flex-start', 
    transition: 'background-color 0.2s ease' 
  },
  historyItemMobile: { padding: '8px 10px' },
  historyItemContent: { flex: 1 },
  historyMeta: { fontSize: '10px', color: '#999', display: 'block', marginTop: '3px' },
  historyMetaMobile: { fontSize: '10px' },
  emptyHistory: { padding: '16px 12px', textAlign: 'center', color: '#999' },
  error: { 
    color: '#dc3545', 
    margin: '6px 0 12px', 
    fontSize: '0.85rem', 
    display: 'block', 
    padding: '6px 10px', 
    backgroundColor: '#ffebee', 
    borderRadius: '4px', 
    borderLeft: '3px solid #dc3545' 
  },
  errorMobile: { fontSize: '0.9rem', padding: '6px 10px' },
  mobileFontTitle: { fontSize: '14px' },
  mobileFontBody: { fontSize: '13px', margin: '4px 0' },
  instructionsContainer: { 
    backgroundColor: '#f8f9fa', 
    border: '1px solid #e9ecef', 
    borderRadius: '6px', 
    padding: '10px 12px', 
    marginBottom: '12px', 
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)', 
    transition: 'all 0.2s ease' 
  },
  instructionsCollapsed: { padding: '8px 12px', cursor: 'pointer' },
  instructionsHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' },
  expandCollapseIcon: { color: '#666', fontSize: '10px' },
  instructionTitle: { marginTop: 0, marginBottom: '8px', fontSize: '14px', color: '#333' },
  instructionList: { marginLeft: '16px', paddingLeft: '0', lineHeight: '1.4', fontSize: '13px' },
  instructionNote: { marginTop: '8px', color: '#666', fontStyle: 'italic', fontSize: '12px' },
  instructionActions: { marginTop: '12px', display: 'flex', justifyContent: 'flex-end', gap: '8px' },
  instructionButton: { 
    backgroundColor: '#4285f4', 
    color: 'white', 
    border: 'none', 
    borderRadius: '4px', 
    padding: '6px 12px', 
    cursor: 'pointer', 
    fontSize: '13px' 
  },
  debugInfo: { 
    position: 'fixed', 
    bottom: '10px', 
    left: '10px', 
    backgroundColor: 'rgba(0,0,0,0.8)', 
    color: 'white', 
    padding: '4px 8px', 
    borderRadius: '4px', 
    fontSize: '10px', 
    zIndex: 9999 
  }
};

export default PushNotificationComponent;